/**
 * E06-F08 — Jev triage of the long-list: shared contracts.
 *
 * Spec: docs/superpowers/specs/2026-10-20-research-jev-triage-design.md §5.3 «Jev-триаж
 * long-list». E06-F08-T01: the PREVIEW the confirmation dialog shows before a triage run — how
 * many rows go, how many of them have no website (they go through the free search, §5.8), and
 * the price estimate (average `usage.cost` of past triage calls, or a conservative constant
 * while there is no history — then flagged as a fallback estimate).
 *
 * A row's triage result lives under the private `evidence.__discovery.triage` key (written by
 * the triage job, E06-F08-T02). A row with that key, or with a stage-0 prefilter flag, is NOT
 * counted (spec: «по умолчанию она берёт все непомеченные строки заявки без результата триажа»).
 */
import { z } from 'zod';
/** Where the per-row price of the preview comes from. */
export const TRIAGE_PRICE_SOURCES = ['history', 'constant'];
/** GET /research/:requestId/review/triage/preview */
export const triagePreviewResponseSchema = z.object({
    success: z.literal(true).optional(),
    requestId: z.number().int().positive(),
    /** Rows that a triage run would send (unflagged, no triage result yet). */
    rows: z.number().int().nonnegative(),
    /** Of `rows`: rows without a website. */
    withoutWebsite: z.number().int().nonnegative(),
    /** Of `rows`: rows that go through the free search (§5.8) — today exactly the rows without a website. */
    viaSearch: z.number().int().nonnegative(),
    /** Rows of the request left out because a prefilter flag is on them. */
    skippedPrefiltered: z.number().int().nonnegative(),
    /** Rows of the request left out because they already have a triage result. */
    skippedTriaged: z.number().int().nonnegative(),
    /** Price of one triage decision in USD (average of past calls, or the constant). */
    costPerRowUsd: z.number().nonnegative(),
    /** `rows × costPerRowUsd`, USD. Always an estimate. */
    estimatedCostUsd: z.number().nonnegative(),
    priceSource: z.enum(TRIAGE_PRICE_SOURCES),
    /** True when there is no call history and the price is the conservative constant («оценка»). */
    priceIsFallbackEstimate: z.boolean(),
    /** How many past priced triage calls the average is built on (0 → constant). */
    priceSampleSize: z.number().int().nonnegative(),
    /**
     * E06-F10-T01 — ambiguous (row × Library company) pairs of these rows: the same run asks Jev
     * `research_same_company` once per pair (spec §5.5 «их число входит в подтверждение»). Included
     * in `estimatedCostUsd`. Optional (older payloads).
     */
    ambiguousPairs: z.number().int().nonnegative().optional(),
});
// ---------------------------------------------------------------------------
// E06-F08-T02 — the triage job: per-row result, start, progress
// ---------------------------------------------------------------------------
/**
 * Buckets a triage result puts a row into (spec §5.3). `likely_junk` is produced ONLY after a
 * calibration that set `likelyJunkMinConfidence` (before that the worst outcome is `doubtful`).
 */
export const TRIAGE_BUCKETS = ['check_first', 'check', 'doubtful', 'likely_junk'];
/**
 * Status of a row's triage: `queued` / `running` while the job works on it; `done` = a bucket is
 * set (by Jev, or by code when the site was not found and the search gave nothing);
 * `unavailable` = the one Jev call failed (no automatic retry — «перетриаж» is a fresh click).
 */
export const TRIAGE_ROW_STATUSES = ['queued', 'running', 'done', 'unavailable'];
/** What the homepage step found. */
export const TRIAGE_SITE_STATUSES = ['ok', 'not_found', 'not_opened'];
/** One question's answer as stored on the row (the probabilities the screen shows). */
export const triageAnswerSchema = z.object({
    type: z.enum(['noul', 'choice', 'score']),
    value: z.number().nullable(),
    choice: z.string().nullable(),
    probabilities: z.record(z.string(), z.number()),
    confidence: z.number().nullable(),
});
/** `evidence.__discovery.triage` of a row, as the Review screen gets it (`ReviewRow.triage`). */
export const rowTriageSchema = z.object({
    status: z.enum(TRIAGE_ROW_STATUSES),
    bucket: z.enum(TRIAGE_BUCKETS).nullable(),
    /** Short reason assembled by code from the answers («дистрибьютор, 0,91»). */
    reason: z.string().nullable(),
    /** Per-question answers with probabilities; null when Jev was not asked / failed. */
    answers: z.record(z.string(), triageAnswerSchema).nullable(),
    site: z.enum(TRIAGE_SITE_STATUSES).nullable(),
    /** The homepage address the text came from. */
    homepageUrl: z.string().nullable(),
    runId: z.string(),
    setVersion: z.number().int().nullable(),
    costUsd: z.number().nullable(),
    /** `jev_decisions.id` as a string (bigint), null when no call was made. */
    decisionId: z.string().nullable(),
    /**
     * E06-F08-T03 — re-triage: the `decisionId` of the result this one replaced (its probabilities
     * stay in `jev_decisions`, append-only). Absent / null on a first triage.
     */
    previousDecisionId: z.string().nullable().optional(),
    /**
     * E06-F09-T02 — the free search (§5.8) when the site was not enough: the query, how it went
     * and the snippet addresses («по поисковой выдаче, не с сайта компании»). Absent / null when
     * no search ran.
     */
    search: z
        .object({
        query: z.string(),
        status: z.enum(['found', 'empty', 'unavailable']),
        urls: z.array(z.string()),
    })
        .nullable()
        .optional(),
    /**
     * E07-F10-T01 — a person returned the row from «вероятный мусор» (bucket → `check`); the
     * return is a Jev label. Absent / null = never returned.
     */
    restored: z
        .object({ from: z.enum(TRIAGE_BUCKETS), by: z.string().nullable(), at: z.string() })
        .nullable()
        .optional(),
    error: z.string().nullable(),
    by: z.string().nullable(),
    queuedAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
});
/** POST /research/:requestId/review/triage — 202: the job was started in the background. */
export const triageStartResponseSchema = z.object({
    success: z.literal(true).optional(),
    requestId: z.number().int().positive(),
    runId: z.string(),
    /** Rows queued for this run. */
    queued: z.number().int().nonnegative(),
    skippedPrefiltered: z.number().int().nonnegative(),
    skippedTriaged: z.number().int().nonnegative(),
    /** Rows left out because the AI check is running on them right now. */
    skippedBusy: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// E06-F08-T03 — re-triage of SELECTED rows (spec §5.3 «Повторный запуск»)
// ---------------------------------------------------------------------------
/** Upper bound of one selection (a long-list is a few thousand rows at most). */
export const TRIAGE_RETRIAGE_MAX_ROWS = 5000;
const retriageRowIdsSchema = z
    .array(z.number().int().positive())
    .min(1)
    .max(TRIAGE_RETRIAGE_MAX_ROWS);
/** POST /research/:requestId/review/triage/retriage/preview — body (read-only, no Jev call). */
export const triageRetriagePreviewBodySchema = z.object({ rowIds: retriageRowIdsSchema });
/**
 * The same confirmation as the default run (rows, rows via search, price) for the selected rows.
 * `rows` = selected rows that WILL be sent; `replacing` = of them, rows whose old result is
 * replaced. `skippedTriaged` is always 0 here (an existing result is exactly what re-triage
 * replaces).
 */
export const triageRetriagePreviewResponseSchema = triagePreviewResponseSchema.extend({
    /** Distinct row ids asked for. */
    selected: z.number().int().nonnegative(),
    replacing: z.number().int().nonnegative(),
    /** Selected rows the AI check is running on right now (they are left out). */
    skippedBusy: z.number().int().nonnegative(),
    /** Selected ids that are not rows of this request. */
    skippedNotFound: z.number().int().nonnegative(),
});
/**
 * POST /research/:requestId/review/triage/retriage — body. `confirmedRows` is the `rows` number
 * the confirmation dialog showed; when the server would now send a different number the run is
 * refused (409) and the dialog must be reopened — no Jev call without a confirmed count + price.
 */
export const triageRetriageBodySchema = z.object({
    rowIds: retriageRowIdsSchema,
    confirmedRows: z.number().int().nonnegative(),
});
/** POST /research/:requestId/review/triage/retriage — 202. */
export const triageRetriageResponseSchema = triageStartResponseSchema.extend({
    /** Of `queued`: rows whose previous result was replaced. */
    replaced: z.number().int().nonnegative(),
    skippedNotFound: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// E06-F10-T01 — ambiguous pairs with the Library: Jev hint + the human «связать» (spec §5.5)
// ---------------------------------------------------------------------------
/**
 * Why a (request row × Library company) pair is ambiguous: `name` = same normalized name, both
 * domains known and different; `subdomain` = the row's domain is a subdomain of the company's;
 * `regional` = same brand label under another public suffix (acme.de × acme.com).
 */
export const SAME_COMPANY_PAIR_KINDS = ['name', 'subdomain', 'regional'];
/** Jev's options of `research_same_company.relation`. */
export const SAME_COMPANY_RELATIONS = ['same', 'division', 'different'];
/** One Jev hint for one pair — a hint only, it never changes the row's link. */
export const sameCompanyHintSchema = z.object({
    companyId: z.number().int().positive(),
    companyName: z.string(),
    companyDomain: z.string().nullable(),
    kind: z.enum(SAME_COMPANY_PAIR_KINDS),
    /** `ok` = Jev answered; `unavailable` = the one call failed (no automatic retry). */
    status: z.enum(['ok', 'unavailable']),
    /** The winning option and its probability (null when unavailable). */
    relation: z.enum(SAME_COMPANY_RELATIONS).nullable(),
    probability: z.number().nullable(),
    probabilities: z.record(z.string(), z.number()),
    /** The set's band: `possible_same` | `possible_division` | `no_hint` (null when unavailable). */
    band: z.string().nullable(),
    /** `jev_decisions.id` as a string (bigint). */
    decisionId: z.string().nullable(),
    costUsd: z.number().nullable(),
});
/** `evidence.__discovery.sameCompany` of a row (`ReviewRow.sameCompany`). */
export const rowSameCompanySchema = z.object({
    runId: z.string(),
    setVersion: z.number().int().nullable(),
    checkedAt: z.string(),
    hints: z.array(sameCompanyHintSchema),
});
/** `evidence.__discovery.libraryLink` — the last human «связать» of the row (who / when / from). */
export const rowLibraryLinkSchema = z.object({
    companyId: z.number().int().positive(),
    previousCompanyId: z.number().int().positive().nullable(),
    by: z.string().nullable(),
    at: z.string(),
});
/** POST /research/:requestId/review/:rowId/link-company — body. */
export const linkRowCompanyBodySchema = z.object({
    companyId: z.number().int().positive(),
});
/** GET /research/:requestId/review/triage/progress — polled by the screen, like AI-check rows. */
export const triageProgressResponseSchema = z.object({
    success: z.literal(true).optional(),
    requestId: z.number().int().positive(),
    /** The latest run of the request (null = never triaged). */
    runId: z.string().nullable(),
    /** True while rows of the latest run are queued or running. */
    active: z.boolean(),
    /** Latest run: rows in it, and how many are in each status. */
    total: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
    /** Every finished row of the request, by bucket. */
    buckets: z.object({
        check_first: z.number().int().nonnegative(),
        check: z.number().int().nonnegative(),
        doubtful: z.number().int().nonnegative(),
        likely_junk: z.number().int().nonnegative(),
    }),
    /** Sum of the known Jev prices of the latest run, USD. */
    costUsd: z.number().nonnegative(),
});
//# sourceMappingURL=research-triage.js.map